/**
 * Shared Identity Utils
 *
 * Local copy of shared-identity functions for use in marketplace.
 * Enables cross-app user recognition with wallet.
 */

/**
 * Simple hash function for ID generation
 * @param {string} str - String to hash
 * @returns {string} - 12-character hash
 */
function simpleHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36).padStart(12, '0').slice(0, 12);
}

/**
 * Generate a canonical Pentatonic user ID
 * @param {string} email - User email
 * @returns {string} - Pentatonic user ID
 */
export function generatePentatonicId(email) {
  const hash = simpleHash(email.toLowerCase().trim());
  return `ptc_user_${hash}`;
}

/**
 * Storage keys for cross-app identity
 */
export const STORAGE_KEYS = {
  PENTATONIC_IDENTITY: 'pentatonic_identity',
  WALLET_SESSION: 'pentatonic_session',
  MARKETPLACE_LINKED: 'pentatonic_marketplace_linked',
  BRIDGE_TOKEN: 'pentatonic_bridge_token',
};

/**
 * Create a cross-app identity payload
 *
 * @param {Object} params - Identity params
 * @param {string} params.email - User email
 * @param {string} params.displayName - Display name
 * @param {string} params.avatarUrl - Avatar URL
 * @param {string} params.source - Source app (marketplace/wallet)
 * @param {string} params.sourceId - Original user ID from source
 * @returns {Object} - Identity token payload
 */
export function createIdentityPayload({
  email,
  displayName = '',
  avatarUrl = '',
  source,
  sourceId,
}) {
  const ptcId = generatePentatonicId(email);

  return {
    ptc_id: ptcId,
    email: email.toLowerCase().trim(),
    display_name: displayName || email.split('@')[0],
    avatar_url: avatarUrl || '',
    source,
    source_id: sourceId,
    wallet_connected: false,
    wallet_address_count: 0,
    wallet_payment_method_count: 0,
    created_at: new Date().toISOString(),
    linked_at: null,
  };
}

/**
 * Merge identity data from multiple sources
 * @param {Object} marketplaceIdentity - Identity from marketplace
 * @param {Object} walletIdentity - Identity from wallet
 * @returns {Object} - Merged identity
 */
export function mergeIdentities(marketplaceIdentity, walletIdentity) {
  return {
    ptc_id: marketplaceIdentity.ptc_id || walletIdentity.ptc_id,
    email: marketplaceIdentity.email || walletIdentity.email,
    display_name: marketplaceIdentity.display_name || walletIdentity.display_name,
    avatar_url: marketplaceIdentity.avatar_url || walletIdentity.avatar_url,
    source: 'linked',
    source_id: marketplaceIdentity.source_id,
    wallet_source_id: walletIdentity.source_id,
    wallet_connected: true,
    wallet_address_count: walletIdentity.wallet_address_count || 0,
    wallet_payment_method_count: walletIdentity.wallet_payment_method_count || 0,
    created_at: marketplaceIdentity.created_at,
    linked_at: new Date().toISOString(),
  };
}

/**
 * Get stored identity from localStorage
 * @returns {Object|null} - Stored identity or null
 */
export function getStoredIdentity() {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.PENTATONIC_IDENTITY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Store identity in localStorage
 * @param {Object} identity - Identity to store
 */
export function storeIdentity(identity) {
  try {
    localStorage.setItem(STORAGE_KEYS.PENTATONIC_IDENTITY, JSON.stringify(identity));
  } catch (e) {
    console.error('[SharedIdentity] Failed to store identity:', e);
  }
}

/**
 * Clear stored identity
 */
export function clearStoredIdentity() {
  try {
    localStorage.removeItem(STORAGE_KEYS.PENTATONIC_IDENTITY);
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_TOKEN);
  } catch (e) {
    console.error('[SharedIdentity] Failed to clear identity:', e);
  }
}

export default {
  generatePentatonicId,
  createIdentityPayload,
  mergeIdentities,
  getStoredIdentity,
  storeIdentity,
  clearStoredIdentity,
  STORAGE_KEYS,
};
